import { Button } from "@/components/ui/button";
import { Car, Trophy, ArrowRight, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ModuleFeedbackCard from "@/components/ai/ModuleFeedbackCard";
import { DecisionEntry, fmt } from "@/hooks/useVehicleSim";

interface Recap {
  totalWallet: number;
  totalScore: number;
  readiness: string;
  strongest: DecisionEntry | null;
  weakest: DecisionEntry | null;
  tradeoff: string;
  takeaway: string;
  nextAction: string;
}

interface Props {
  purchaseCompleted: boolean;
  vehicleName?: string;
  purchaseGrade?: string | null;
  affordabilityRating?: string | null;
  reasonMismatch?: string | null;
  decisionLog?: DecisionEntry[];
  recap?: Recap;
  onTryAgain: () => void;
}

const GRADE_SUMMARIES: Record<string, string> = {
  'A': 'Outstanding — sound math and a vehicle that matched the stated need.',
  'A-': 'Strong — one minor optimization away from ideal.',
  'B+': 'Very good. Needs and numbers both held up.',
  'B': 'Solid. Your finances can handle this, with room to tighten.',
  'C+': 'Acceptable, but this purchase quietly pressures your cushion.',
  'C': 'This purchase stretches your budget. A cheaper option would protect future flexibility.',
  'D': 'This decision weakens your financial position. The math is the lesson.',
};

export default function CompletionScreen({
  purchaseCompleted, vehicleName, purchaseGrade, affordabilityRating, reasonMismatch,
  decisionLog = [], recap, onTryAgain,
}: Props) {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="glass-card glow-border max-w-md w-full p-6 sm:p-8 text-center space-y-5">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto">
          {purchaseCompleted ? <Car className="w-8 h-8 text-accent" /> : <Trophy className="w-8 h-8 text-accent" />}
        </div>

        {purchaseCompleted ? (
          <>
            <h1 className="font-heading text-xl font-bold text-foreground">Vehicle Acquired</h1>
            {vehicleName && <p className="text-foreground font-semibold">{vehicleName}</p>}
            {purchaseGrade && (
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-heading font-bold">
                  Purchase Grade: {purchaseGrade}
                </div>
                <p className="text-sm text-muted-foreground">{GRADE_SUMMARIES[purchaseGrade] || 'Decision recorded.'}</p>
              </div>
            )}
          </>
        ) : (
          <>
            <h1 className="font-heading text-xl font-bold text-foreground">Walked Away</h1>
            <p className="text-sm text-muted-foreground">
              No loan, no insurance change, no new monthly bill. Sometimes the best money move is the one you do not make.
            </p>
          </>
        )}

        {recap && (
          <div className="space-y-3 text-left">
            <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-primary/80">Readiness label</p>
              <p className="font-heading font-bold text-base text-foreground">{recap.readiness}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-2xl border border-border bg-card/40 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Wallet impact</p>
                <p className={`font-heading font-bold ${recap.totalWallet < 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                  {recap.totalWallet < 0 ? '−' : '+'}{fmt(Math.abs(recap.totalWallet))}
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-card/40 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Judgment score</p>
                <p className="font-heading font-bold text-foreground">{recap.totalScore >= 0 ? '+' : ''}{recap.totalScore}</p>
              </div>
            </div>
            {recap.strongest && (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-500/80">Strongest decision</p>
                <p className="text-sm font-semibold text-foreground">{recap.strongest.label}: {recap.strongest.choice}</p>
                <p className="text-xs text-muted-foreground italic">{recap.strongest.lumiSignal}</p>
              </div>
            )}
            {recap.weakest && recap.weakest !== recap.strongest && (
              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-amber-500/80">Weakest decision</p>
                <p className="text-sm font-semibold text-foreground">{recap.weakest.label}: {recap.weakest.choice}</p>
                <p className="text-xs text-muted-foreground italic">{recap.weakest.lumiSignal}</p>
              </div>
            )}
            <div className="rounded-2xl border border-border bg-card/40 px-3 py-2 space-y-1">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Key tradeoff</p>
              <p className="text-sm text-foreground">{recap.tradeoff}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card/40 px-3 py-2 space-y-1">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Real-world takeaway</p>
              <p className="text-sm text-foreground">{recap.takeaway}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card/40 px-3 py-2 space-y-1">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Suggested next mission</p>
              <p className="text-sm text-foreground">{recap.nextAction}</p>
            </div>
            {decisionLog.length > 0 && (
              <details className="rounded-2xl border border-border bg-card/40 px-3 py-2">
                <summary className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground cursor-pointer">
                  Full decision log ({decisionLog.length})
                </summary>
                <ul className="space-y-2 pt-2">
                  {decisionLog.map((d, i) => (
                    <li key={i} className="text-xs border-l-2 border-border pl-2">
                      <p className="font-semibold text-foreground">{d.label} — {d.choice}</p>
                      <p className="text-muted-foreground">{d.consequence}</p>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {reasonMismatch && (
          <div className="bg-amber-500/10 p-3 rounded-xl text-xs text-amber-600 text-left">
            <p className="font-semibold">Carry this forward:</p>
            <p>{reasonMismatch}</p>
          </div>
        )}

        <ModuleFeedbackCard
          moduleName="Vehicle Ownership Simulator"
          moduleKey="vehicle_ownership_sim"
          finalMetrics={{
            purchase_completed: purchaseCompleted,
            vehicle_name: vehicleName,
            purchase_grade: purchaseGrade,
            affordability_rating: affordabilityRating,
            decision_count: decisionLog.length,
            total_wallet_impact: recap?.totalWallet,
            total_score: recap?.totalScore,
            readiness: recap?.readiness,
          }}
          outcome={purchaseCompleted ? (purchaseGrade && purchaseGrade <= "B" ? "strong" : "moderate") : "walked_away"}
        />

        <div className="flex flex-col gap-3">
          <Button onClick={() => navigate("/finances")} className="w-full gap-2">View Finances <ArrowRight className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={onTryAgain} className="w-full gap-2"><RotateCcw className="w-4 h-4" /> Start a New Run</Button>
          <Button variant="outline" onClick={() => navigate("/")} className="w-full">Back to Dashboard</Button>
        </div>
      </div>
    </div>
  );
}
